import { CommunityOrgTypeSentinel, PromptId } from "@sp-crm/core";
import { Checkbox } from "components/ui/checkbox";
import { FilterPanelTrigger } from "components/ui/filter-panel-trigger";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { SearchInput } from "components/ui/search-input";
import React, { useMemo, useState } from "react";
import { useCommunityTypeQuestion, useRegionId, useRegions } from "store/selectors/hooks";
import {
    ClearAllLink,
    OrganizationTypeIcon,
    ReferralTypeChecklistGroups,
} from "./org-type-checklist";

interface OrgTypeFilterProps {
    value: string[];
    onChange: (orgTypes: string[]) => void;
    communityTypes: PromptId[];
    onCommunityTypesChange: (communityTypes: PromptId[]) => void;
    onClear: () => void;
}

export const OrgTypeFilter: React.FC<OrgTypeFilterProps> = props => {
    const { value, onChange, communityTypes, onCommunityTypesChange, onClear } = props;

    const [showPanel, setShowPanel] = useState(false);
    const [itemFilterText, setItemFilterText] = useState("");

    const regionId = useRegionId();
    const regions = useRegions();
    const region = regions.find(r => r.id === regionId);
    const regionKey = region?.key;
    const communityTypeQuestion = useCommunityTypeQuestion(regionKey, regionId);

    const addOrgType = (orgType: string) => {
        if (!value.includes(orgType)) {
            const newOrgTypes = [...value, orgType];
            onChange(newOrgTypes);
        }
    };

    const removeOrgType = (orgType: string) => {
        if (value.includes(orgType)) {
            const newOrgTypes = value.filter(ot => ot !== orgType);
            onChange(newOrgTypes);
        }
    };

    const addCommunityType = (communityType: PromptId) => {
        if (!communityTypes.includes(communityType)) {
            const newCommunityTypes = [...communityTypes, communityType];
            onCommunityTypesChange(newCommunityTypes);
        }
    };

    const removeCommunityType = (communityType: PromptId) => {
        if (communityTypes.includes(communityType)) {
            const newCommunityTypes = communityTypes.filter(ct => ct !== communityType);
            onCommunityTypesChange(newCommunityTypes);
        }
    };

    const showRecordTypeOption = useMemo(() => {
        return (
            "Record Type".toLowerCase().includes(itemFilterText.toLowerCase()) ||
            "Community".toLowerCase().includes(itemFilterText.toLowerCase())
        );
    }, [itemFilterText]);

    const filteredPrompts = useMemo(() => {
        if (!communityTypeQuestion) return [];
        return communityTypeQuestion.prompts.filter(
            p =>
                communityTypeQuestion.title
                    .toLowerCase()
                    .includes(itemFilterText.toLowerCase()) ||
                p.title.toLowerCase().includes(itemFilterText.toLowerCase()),
        );
    }, [communityTypeQuestion, itemFilterText]);

    return (
        <>
            <FilterPanelTrigger
                icon={<OrganizationTypeIcon />}
                label="Organization type"
                selectedCount={value.length + communityTypes.length}
                onOpen={() => setShowPanel(true)}
            />
            <ResponsivePanel
                headerText="Organization type"
                type={PanelType.medium}
                isOpen={showPanel}
                onDismiss={() => {
                    setShowPanel(false);
                    setItemFilterText("");
                }}>
                <div className="space-y-4">
                    <SearchInput
                        value={itemFilterText}
                        onChange={e => {
                            setItemFilterText(e.target.value);
                        }}
                        onClear={() => setItemFilterText("")}
                    />
                    <ClearAllLink onClear={onClear} />
                    <ul>
                        <ReferralTypeChecklistGroups
                            filterText={itemFilterText}
                            value={value}
                            onCheck={addOrgType}
                            onUncheck={removeOrgType}
                        />
                        {communityTypeQuestion &&
                            filteredPrompts &&
                            filteredPrompts.length > 0 && (
                                <li>
                                    <p className="font-bold">
                                        {communityTypeQuestion.title}
                                    </p>
                                    <ul>
                                        {filteredPrompts.map(prompt => (
                                            <li key={prompt.id}>
                                                <Checkbox
                                                    checked={communityTypes.includes(
                                                        prompt.id,
                                                    )}
                                                    onChange={e => {
                                                        e.target.checked
                                                            ? addCommunityType(prompt.id)
                                                            : removeCommunityType(
                                                                  prompt.id,
                                                              );
                                                    }}
                                                    label={prompt.title}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            )}
                        {showRecordTypeOption && (
                            <li>
                                <p className="font-bold">Record Type</p>
                                <ul>
                                    <li>
                                        <Checkbox
                                            checked={value.includes(
                                                CommunityOrgTypeSentinel,
                                            )}
                                            onChange={e => {
                                                e.target.checked
                                                    ? addOrgType(CommunityOrgTypeSentinel)
                                                    : removeOrgType(
                                                          CommunityOrgTypeSentinel,
                                                      );
                                            }}
                                            label="Include All Communities"
                                        />
                                    </li>
                                </ul>
                            </li>
                        )}
                    </ul>
                </div>
            </ResponsivePanel>
        </>
    );
};
